/**
 * 計測 1 回 = /api/run 1 リクエスト（complexity-meter.md §2）。
 * planMeasurement → viz 補助の連結 → `class.run` span の下で送信 → collectMeterResult。
 * sandboxd クライアントは引数で受け取る（画面からは services.ts の sandbox を渡す）。
 */
import {
  collectMeterResult,
  planMeasurement,
  type MeterPlan,
  type MeterResult,
  type MeterSpec,
} from '../../lib/complexity';
import {
  isAbortError,
  retryNotice,
  SandboxError,
  type RunResponse,
  type SandboxClient,
} from '../../lib/sandboxd';
import {
  recordRunOutcome,
  withSpan,
  type RunOutcome,
  type RunPurpose,
  type Span,
} from '../../lib/telemetry';
import { filterCompileOutput, withVizHelper } from '../../lib/viz';

/** `class.meter.*` 属性（値は列挙と数値だけ。コードや入出力は入れない）。 */
export interface MeterSpanInfo {
  mode: 'compare' | 'own';
  experiment?: string;
  variant?: 'slow' | 'fast';
}

export interface MeterRunArgs {
  code: string;
  meter: MeterSpec;
  purpose: RunPurpose;
  span: MeterSpanInfo;
  /** 再計測（durationMs を取り直す）。 */
  bypassCache?: boolean;
  signal?: AbortSignal;
  onRetry?: (notice: string) => void;
}

export type MeterRunOutcome =
  | {
      kind: 'ok';
      result: MeterResult;
      plan: MeterPlan;
      /** ケースごとの標準出力（plan.sizes と同じ順）。 */
      stdouts: string[];
      /** どのケースにも viz.tick の記録が無い。 */
      noTicks: boolean;
    }
  | { kind: 'compile-error'; output: string; helperConflict: boolean }
  | { kind: 'nothing-to-send'; skipped: MeterPlan['skipped'] }
  | { kind: 'error'; message: string; retryable: boolean }
  | { kind: 'aborted' };

/** 応答から運用向けの結果を決める（observability.md §6.1）。 */
export function outcomeOfResponse(res: RunResponse): RunOutcome {
  if (res.compile?.ok !== true) return 'compile_error';
  const runs = res.runs ?? [];
  if (runs.some((r) => r.timedOut)) return 'timeout';
  if (runs.some((r) => r.exitCode !== 0)) return 'runtime_error';
  return 'completed';
}

/** SandboxError の種類から運用向けの結果を決める。 */
export function outcomeOfError(e: SandboxError): RunOutcome {
  switch (e.kind) {
    case 'busy':
      return e.status === 429 ? 'rate_limited' : 'busy';
    case 'timeout':
      return 'gateway_timeout';
    case 'network':
      return 'network_error';
    default:
      return 'http_error';
  }
}

function setMeterAttributes(span: Span, info: MeterSpanInfo, plan: MeterPlan): void {
  span.setAttribute('class.meter.mode', info.mode);
  if (info.experiment !== undefined) span.setAttribute('class.meter.experiment', info.experiment);
  if (info.variant !== undefined) span.setAttribute('class.meter.variant', info.variant);
  span.setAttribute('class.meter.sizes', plan.sizes.length);
  span.setAttribute('class.meter.skipped', plan.skipped.length);
  span.setAttribute('class.meter.max_n', plan.sizes[plan.sizes.length - 1] ?? 0);
}

export async function runMeter(
  client: Pick<SandboxClient, 'run'>,
  args: MeterRunArgs,
): Promise<MeterRunOutcome> {
  const plan = planMeasurement(args.meter);
  if (plan.sizes.length === 0) return { kind: 'nothing-to-send', skipped: plan.skipped };
  const attached = withVizHelper(args.code);
  const req = { code: attached.code, stdins: plan.stdins };
  return withSpan(
    'class.run',
    {
      'class.run.purpose': args.purpose,
      'class.run.cases': req.stdins.length,
      'class.run.code_bytes': new TextEncoder().encode(req.code).length,
    },
    async (span): Promise<MeterRunOutcome> => {
      setMeterAttributes(span, args.span, plan);
      let res: RunResponse;
      try {
        res = await client.run(req, {
          cache: args.bypassCache === true ? 'bypass' : 'use',
          ...(args.signal ? { signal: args.signal } : {}),
          onRetry: (attempt) => args.onRetry?.(retryNotice(attempt)),
        });
      } catch (e) {
        if (isAbortError(e)) return { kind: 'aborted' };
        if (e instanceof SandboxError) {
          if (e.kind === 'local-busy')
            return { kind: 'error', message: e.message, retryable: false };
          recordRunOutcome(span, outcomeOfError(e));
          return { kind: 'error', message: e.message, retryable: e.kind !== 'rejected' };
        }
        throw e;
      }
      recordRunOutcome(span, outcomeOfResponse(res));
      const collected = collectMeterResult(plan, res);
      if (!collected.ok) {
        const filtered = attached.attached
          ? filterCompileOutput(collected.compileOutput, attached.userLineCount)
          : { output: collected.compileOutput, helperConflict: false };
        return {
          kind: 'compile-error',
          output: filtered.output,
          helperConflict: filtered.helperConflict,
        };
      }
      const stdouts = plan.sizes.map((_, i) => res.runs?.[i]?.stdout ?? '');
      const noTicks = collected.result.points.every((p) => p.ticks === null);
      span.setAttribute(
        'class.meter.timed_out',
        collected.result.points.filter((p) => p.timedOut).length,
      );
      return { kind: 'ok', result: collected.result, plan, stdouts, noTicks };
    },
  );
}
